import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { getUser } from './auth';
import { toast } from 'sonner';

// Determine the correct backend URL for WebSocket
const getBackendUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  
  const origin = window.location.origin;
  
  // If we're on emergent.host or safer-i.com (production), use current origin
  if (origin.includes('.emergent.host') || origin.includes('.emergent.sh') || origin.includes('safer-i.com')) {
    return origin;
  }
  
  // Otherwise (localhost/development), use env variable
  return process.env.REACT_APP_BACKEND_URL || origin;
};

const BACKEND_URL = getBackendUrl();

export function useWebSocket() {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const user = getUser();
    if (!user) return;

    // Initialize socket connection
    socketRef.current = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
      setConnected(true);
      
      // Join organization room
      socket.emit('join_room', {
        org_id: user.org_id,
        user_id: user.user_id
      });
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });


    // Listen for task assignments
    socket.on('task_assigned', (data) => {
      toast.info(`New Task: ${data.title}`, {
        description: `Assigned by ${data.assigned_by}`,
        action: {
          label: 'View',
          onClick: () => window.location.href = '/tasks'
        }
      });
      setNotifications(prev => [...prev, { type: 'task_assigned', data, timestamp: new Date() }]);
    });

    // Listen for form submissions
    socket.on('form_submitted', (data) => {
      setNotifications(prev => [...prev, { type: 'form_submitted', data, timestamp: new Date() }]);
    });

    // Listen for ISO alerts
    socket.on('iso_alert', (data) => {
      toast.warning('ISO 45001 Alert', {
        description: data.message || 'Audit readiness requires attention'
      });
      setNotifications(prev => [...prev, { type: 'iso_alert', data, timestamp: new Date() }]);
    });

    socket.on('error', (error) => {
      console.error('WebSocket error:', error);
    });

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
    // BACKEND_URL is a module-level constant, not a valid hook dependency.
     
  }, []);

  return {
    connected,
    notifications,
    clearNotifications: () => setNotifications([])
  };
}
